import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  CreateWorkerCounterTemplateParams,
  FetchWorkerCounterTemplatesParams,
  UpdateWorkerCounterTemplateParams,
  WorkerCounterTemplate,
  create,
  fetch,
  get,
  update,
} from '../api/worker-counter-templates'

/**
 * Query hook for fetching worker counter templates list
 * @param filters - Optional filters for the query
 * @param options - Optional query options
 * @returns Query result with templates array
 */
export const useWorkerCounterTemplatesQuery = (
  filters?: FetchWorkerCounterTemplatesParams,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounterTemplate[]> =>
  useResources<WorkerCounterTemplate[], FetchWorkerCounterTemplatesParams>(
    'worker-counter-templates',
    fetch,
    filters || {},
    options,
  )

/**
 * Query hook for fetching a single worker counter template
 * @param id - The template ID to fetch
 * @returns Query result with the template
 */
export const useWorkerCounterTemplateQuery = (
  id?: string,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounterTemplate> =>
  useResource('worker-counter-templates', get, id, undefined, {
    enabled: Boolean(id),
    ...options,
  })

export const workerCounterTemplateKeys = {
  all: ['worker-counter-templates'] as const,
  mutations: {
    create: ['worker-counter-templates', 'create'] as const,
    update: ['worker-counter-templates', 'update'] as const,
  },
} as const

/**
 * Callback function to invalidate related queries after mutations
 */
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['worker-counter-templates'])
}

/**
 * Mutation hook for creating a new worker counter template
 * @returns Mutation result for template creation
 */
export const useCreateWorkerCounterTemplateMutation = (): UseMutationResult<
  WorkerCounterTemplate,
  CreateWorkerCounterTemplateParams
> =>
  useMutation(create, {
    mutationKey: workerCounterTemplateKeys.mutations.create,
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook for updating a worker counter template
 * @returns Mutation result for template update
 */
export const useUpdateWorkerCounterTemplateMutation = (): UseMutationResult<
  WorkerCounterTemplate,
  UpdateWorkerCounterTemplateParams
> =>
  useMutation(update, {
    mutationKey: workerCounterTemplateKeys.mutations.update,
    onSuccess: onMutationSuccess,
  })
